import {memo, useCallback, useEffect, useState} from 'react';
import {Box, Grid, Stack, Typography} from '@mui/material';
import Button from '@mui/material/Button';
import {
  DataGrid,
  GridColDef,
  gridPageCountSelector,
  GridPagination, GridToolbar, useGridApiContext,
  useGridApiRef, useGridSelector
} from '@mui/x-data-grid';

import {getCorRuns, getCrToDt, getDtToCr, getScTranges, getSimulation} from '../../../utils/api';
import MhdDatetime from '../../atomicComponents/MhdDatetime';
import dayjs from 'dayjs';
import MhdInput from '../../atomicComponents/MhdInput';
import {
  corModelFilters,
  runTypeFilters,
  solutionChoices,
  timeOfInterestEventsFilters,
  variableChoices
} from '../../../utils/choices';
import MhdMultiSelect from '../../atomicComponents/MhdMultiSelect';

import MuiPagination from '@mui/material/Pagination';
import {useTheme} from '@mui/material/styles';
import MhdIconMenu from '../../atomicComponents/MhdIconMenu';

import {setAndUpdate} from "../../../utils/helpers";
import StateContainer from "./StateContainer";

function Pagination({ page, onPageChange, className }) {
  const apiRef = useGridApiContext();
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <MuiPagination
      color='primary'
      className={className}
      count={pageCount}
      page={page + 1}
      onChange={(event, newPage) => {
        onPageChange(event, newPage - 1);
      }}
    />
  );
}

function CustomPagination(props) {
  return <GridPagination ActionsComponent={Pagination} {...props} />;
}


const MasDatabase = ({corId, setCorId, corData}) => {
  const corCols: GridColDef[] = [
    { field: 'id',
      headerName: 'ID',
      type: 'number',
      width: 40
    },
    { field: 'name',
      headerName: 'Name',
      type: 'string',
      width: 200
    },
    { field: 'start_time',
      headerName: 'Start Time',
      type: 'dateTime',
      width: 120,
      valueGetter: (value) => value && new Date(value),
      format: (value) => value.toLocaleString('UTC')
    },
    { field: 'end_time',
      headerName: 'End Time',
      type: 'dateTime',
      width: 120,
      valueGetter: (value) => value && new Date(value),
      format: (value) => value.toLocaleString('UTC')
    },
    { field: 'run_type',
      headerName: 'Run Type',
      type: 'string',
      width: 90,
    },
    { field: 'run_descriptor',
      headerName: 'Run Descriptor',
      type: 'string',
      width: 90,
    },
    { field: 'cor_model',
      headerName: 'Coronal Model',
      type: 'string',
      width: 90,
    },
    { field: 'surface_data_source',
      headerName: 'Surface Data Source',
      type: 'string',
      width: 90,
    },
    { field: 'has_cor',
      headerName: 'Coronal Domain',
      type: 'boolean',
      width: 90,
    },
    { field: 'has_hel',
      headerName: 'Heliospheric Domain',
      type: 'boolean',
      width: 90,
    },
    { field: 'nr',
      headerName: 'Radial Resolution',
      type: 'number',
    },
    { field: 'nt',
      headerName: 'Theta Resolution',
      type: 'number',
    },
    { field: 'np',
      headerName: 'Phi Resolution',
      type: 'number',
    },
  ];
  const apiRef = useGridApiRef();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 })
  const [initialPageSet, setInitialPageSet] = useState(false);

  useEffect(() => {
    if (!initialPageSet && corData.length > 0 && corId[0]) {
      const index = corData.findIndex(row => row.id === corId[0]);
      if (index !== -1) {
        const newPage = Math.floor(index / paginationModel.pageSize);
        setPaginationModel({ page: newPage, pageSize: paginationModel.pageSize });

        setTimeout(() => {
          apiRef.current.scrollToIndexes({ rowIndex: index });
        }, 50);

        setInitialPageSet(true);
      }
    }
  }, [corId, corData, initialPageSet]);

  return (
    <Box width={'100%'} height={'600px'}>
      <DataGrid
        rows={corData}
        columns={corCols}
        apiRef={apiRef}
        rowSelectionModel={corId}
        onRowSelectionModelChange={(params) => setCorId(params)}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => setPaginationModel(model)}
        initialState={{
          columns: {
            columnVisibilityModel: {
              id: false,
              has_cor: false,
              has_hel: false,
              nr: false,
              nt: false,
              np: false,
            },
          },
          density: 'compact'
        }}
        sx={{
          '& .MuiDataGrid-footerContainer': {
          backgroundColor: 'base.rothko6',
          }, '& .MuiTablePagination-root': {
            backgroundColor: 'base.rothko6',
          }, '& .MuiTablePagination-toolbar': {
            backgroundColor: 'base.rothko6',
          }, '& .MuiTablePagination-actions': {
            backgroundColor: 'base.rothko6',
          },
        }}
        pageSizeOptions={[5, 10, 20, 50, 100]}
        disableMultipleRowSelection
        checkboxSelection
        slots={{
          toolbar: GridToolbar,
          pagination: CustomPagination,
        }}
      />
    </Box>
  )
}

export default memo(MasDatabase);